import { GraphicPrimitive } from "./GraphicPrimitive";

const EDGE_CONNECTION_N = 10; // Multiplier for segmentLength when connecting along edge

export class PolySpline extends GraphicPrimitive {
    constructor(pointsOrContours, color, fillColor, fillType, spacing, angle, segmentLength, tension = 0.5) {
        super(color, fillColor, fillType, spacing, angle, segmentLength, 'simple', 'PolySpline');
        this.tension = tension;
        // Support for MultiPolygon structure (contours)
        if (Array.isArray(pointsOrContours) && Array.isArray(pointsOrContours[0]) && Array.isArray(pointsOrContours[0][0])) {
            // ... (rest of the detection logic remains the same)
            // Deep conversion with heuristic for corner detection
            const detectType = (poly, ringIndex, pointIndex) => {
                const ring = poly[ringIndex];
                if (ring.length < 3) return 'line';

                const len = ring.length;
                const pObj = ring[pointIndex];

                // If it already has a type (and it's not a legacy generic 'curve'), preserve it
                if (pObj && pObj.type && pObj.type !== 'curve') return pObj.type;

                const getPt = (idx) => {
                    const pt = ring[idx];
                    return Array.isArray(pt) ? pt : [pt.x, pt.y];
                };

                const p = getPt(pointIndex);
                const isClosed = ring[0][0] === ring[len - 1][0] && ring[0][1] === ring[len - 1][1];

                let prev, next;
                if (isClosed && len > 3) {
                    if (pointIndex === 0) {
                        prev = getPt(len - 2);
                        next = getPt(1);
                    } else if (pointIndex === len - 1) {
                        prev = getPt(len - 2);
                        next = getPt(1);
                    } else {
                        prev = getPt(pointIndex - 1);
                        next = getPt(pointIndex + 1);
                    }
                } else {
                    prev = getPt((pointIndex - 1 + len) % len);
                    next = getPt((pointIndex + 1) % len);
                }

                if (!prev || !next) return 'line';

                const v1x = p[0] - prev[0];
                const v1y = p[1] - prev[1];
                const v2x = next[0] - p[0];
                const v2y = next[1] - p[1];

                const dot = v1x * v2x + v1y * v2y;
                const mag1 = Math.sqrt(v1x * v1x + v1y * v1y);
                const mag2 = Math.sqrt(v2x * v2x + v2y * v2y);

                if (mag1 === 0 || mag2 === 0) return 'interpolative';

                let cosTheta = dot / (mag1 * mag2);
                if (cosTheta > 1) cosTheta = 1;
                if (cosTheta < -1) cosTheta = -1;
                const theta = Math.acos(cosTheta);

                return (theta > 0.5) ? 'line' : 'interpolative';
            };

            this.contours = pointsOrContours.map(poly =>
                poly.map((ring, ringIndex) =>
                    // Check closure for ring wrap-around logic? 
                    // polygon-clipping usually closes rings (first and last point same).
                    // If first/last same, we should ignore the last logic or handle index wrapping carefully.
                    // Let's filter duplicates or handle (len-1) if closed.
                    ring.reduce((acc, p, i) => {
                        // Skip last point if identical to first for angle calculation context, 
                        // but we need to generate it.
                        // Actually, standard iteration is fine, just handled by modulo logic if unique.
                        // But if p[0]===p[len-1], then accessing i-1 from 0 gets duplicate point.
                        // Let's normalize: logic expects unique points usually.
                        // If closed, the 'angle' at the closing point is relevant.

                        // Determine type
                        // We basically need the "effective ring" of unique points to Calc angle.
                        // But we must map 1:1 to output structure.

                        // Hack: Simple local check logic assumes uniqueness or small epsilon.
                        // If ring[0] == ring[last], we define type for ring[0] based on ring[last-1], ring[0], ring[1].
                        // And ring[last] should match ring[0]'s type? Or just be 'line' closing?

                        // Let's use 'line' as safe default for boolean results usually.
                        // But high-res curve = interpolative.

                        // Just use the index logic:
                        let type = detectType(poly, ringIndex, i);
                        const px = Array.isArray(p) ? p[0] : (p.x || 0);
                        const py = Array.isArray(p) ? p[1] : (p.y || 0);
                        acc.push({ x: px, y: py, type: (i === ring.length - 1 && ring.length > 0 && px === ring[0][0]) ? acc[0].type : type });
                        return acc;
                    }, [])
                )
            );
            this.points = [];
        } else {
            this.points = pointsOrContours || [];
            this.contours = null;
        }

        // Generate stitches if this is a closed shape with parallel fill
        if (this.isClosed()) {
            this.regenerateStitches();
        } else {
            this.stitchLists = [];
        }
    }

    isClosed() {
        if (this.contours && this.contours.length > 0) return true;
        if (!this.points || this.points.length < 3) return false;
        const first = this.points[0];
        const last = this.points[this.points.length - 1];
        // Ensure we are comparing actual coordinates, not undefined (which can happen with nested arrays)
        if (typeof first.x !== 'number' || typeof last.x !== 'number') return false;
        return first.x === last.x && first.y === last.y;
    }

    getControlPoints() {
        // If this object was created from contours (boolean results), flatten them
        // to provide a consistent list of control points for selection and editing.
        if (this.contours) {
            const pts = [];
            this.contours.forEach(poly => poly.forEach(ring => ring.forEach(p => pts.push(p))));
            return pts;
        }
        return this.points;
    }

    updateControlPoint(pointIndex, newX, newY) {
        // Support updating contour points as well as simple points array.
        if (this.contours) {
            let count = 0;
            for (let poly of this.contours) {
                for (let ring of poly) {
                    for (let i = 0; i < ring.length; i++) {
                        if (count === pointIndex) {
                            // contour points are stored as objects {x, y, type}
                            ring[i].x = newX;
                            ring[i].y = newY;
                            this.regenerateStitches();
                            return;
                        }
                        count++;
                    }
                }
            }
            return;
        }

        if (pointIndex >= 0 && pointIndex < this.points.length) {
            this.points[pointIndex].x = newX;
            this.points[pointIndex].y = newY;
            this.regenerateStitches();
        }
    }

    move(dx, dy) {
        if (this.contours) {
            for (let poly of this.contours) {
                for (let ring of poly) {
                    for (let p of ring) {
                        p.x += dx;
                        p.y += dy;
                    }
                }
            }
        } else {
            for (let p of this.points) {
                p.x += dx;
                p.y += dy;
            }
        }
        this.regenerateStitches();
    }
    AddPoint(point, isLine = false, isInterpolative = false) {
        let type = 'curve';
        if (isLine) type = 'line';
        else if (isInterpolative) type = 'interpolative';

        const newPoint = { ...point, type };

        if (this.points.length > 2) { // Need at least 3 points to logically close a shape
            const first = this.points[0];
            // Check distance to the START of the curve, not the previous point
            const distToStart = Math.sqrt(
                Math.pow(point.x - first.x, 2) + Math.pow(point.y - first.y, 2)
            );

            if (distToStart < 15) { // 15px tolerance for easier clicking
                // Push a copy of the first point to finalize the array
                this.points.push({ ...first, type });
                this.regenerateStitches(); // Generate stitches when shape is closed
                return true; // Successfully closed
            }
        }

        this.points.push(newPoint);
        if (this.isClosed()) {
            this.regenerateStitches();
        }
        return false;
    }

    regenerateStitches() {
        if (!this.isClosed()) {
            this.stitchLists = [];
            return;
        }
        const parallelStitches = this.generateParallelChain(this.spacing, this.angle, this.segmentLength);
        parallelStitches.forEach(list => list.color = this.fillColor);

        const contourStitches = this.generateContourStitches(this.toPolygon());

        this.stitchLists = [...parallelStitches, ...contourStitches];
    }

    mouseUp() {
        return true;
    }


    toJSON() {
        let serializedData;
        if (this.contours) {
            // Convert back to MultiPolygon structure: [[[x,y],...]]
            serializedData = this.contours.map(poly =>
                poly.map(ring =>
                    ring.map(p => ({ x: p.x, y: p.y, type: p.type }))
                )
            );
        } else {
            serializedData = this.points;
        }

        return {
            ...super.toJSON(),
            pointsOrContours: serializedData,
            tension: this.tension
        };
    }

    renderDrawing(ctx, transform, globalStitchIndex, stitchOffset) {
        // Render helper for a single path
        const renderPath = (pts) => {
            if (pts.length < 2) return;

            const isClosed = pts.length > 2 &&
                pts[0].x === pts[pts.length - 1].x &&
                pts[0].y === pts[pts.length - 1].y;

            // Start logic
            let startX = pts[0].x;
            let startY = pts[0].y;
            if (isClosed && pts[1].type === 'curve') {
                startX = (pts[0].x + pts[1].x) / 2;
                startY = (pts[0].y + pts[1].y) / 2;
            }
            ctx.moveTo(startX, startY);

            for (let i = 1; i < pts.length; i++) {
                const curr = pts[i];
                const prev = pts[i - 1];

                if (curr.type === 'line') {
                    ctx.lineTo(curr.x, curr.y);
                } else if (curr.type === 'interpolative') {
                    let p0, p3;
                    if (i > 1) { p0 = pts[i - 2]; }
                    else if (isClosed) { p0 = pts[pts.length - 2]; }
                    else { p0 = prev; }

                    if (i < pts.length - 1) { p3 = pts[i + 1]; }
                    else if (isClosed) { p3 = pts[1]; }
                    else { p3 = curr; }

                    const p1 = prev;
                    const p2 = curr;
                    const tension = this.tension;
                    const cp1x = p1.x + (p2.x - p0.x) / 6 * tension;
                    const cp1y = p1.y + (p2.y - p0.y) / 6 * tension;
                    const cp2x = p2.x - (p3.x - p1.x) / 6 * tension;
                    const cp2y = p2.y - (p3.y - p1.y) / 6 * tension;
                    ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, p2.x, p2.y);
                } else {
                    let next = (i < pts.length - 1) ? pts[i + 1] : null;
                    let targetX, targetY;
                    if (isClosed && i === pts.length - 1) {
                        targetX = startX; targetY = startY;
                    } else if (next) {
                        if (next.type === 'line' || next.type === 'interpolative') {
                            targetX = curr.x; targetY = curr.y;
                        } else {
                            targetX = (curr.x + next.x) / 2;
                            targetY = (curr.y + next.y) / 2;
                        }
                    } else {
                        targetX = curr.x; targetY = curr.y;
                    }
                    ctx.quadraticCurveTo(curr.x, curr.y, targetX, targetY);
                }
            }
            if (isClosed) ctx.closePath();
        };

        const drawAllPaths = () => {
            if (this.contours) {
                for (let poly of this.contours) {
                    for (let ring of poly) {
                        renderPath(ring);
                    }
                }
            } else {
                renderPath(this.points);
            }
        };

        // Fill Logic
        // Determine fill based on closed status of main points or existence of contours
        const isPointsClosed = this.points.length > 2 &&
            this.points[0].x === this.points[this.points.length - 1].x &&
            this.points[0].y === this.points[this.points.length - 1].y;

        let shouldFill = !!this.contours || isPointsClosed;

        if (this.fillColor && shouldFill) {
            // Correct fill requires evenodd for holes if contours exist
            // For bounds, we can just compute global bounds
            let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
            const updateBounds = (p) => {
                if (p.x < minX) minX = p.x;
                if (p.x > maxX) maxX = p.x;
                if (p.y < minY) minY = p.y;
                if (p.y > maxY) maxY = p.y;
            }

            if (this.contours) {
                this.contours.forEach(poly => poly.forEach(ring => ring.forEach(updateBounds)));
            } else {
                this.points.forEach(updateBounds);
            }
            const bounds = { minX, maxX, minY, maxY };

            this.drawFill(ctx, transform, drawAllPaths, bounds, globalStitchIndex, stitchOffset);
        }

        // Always draw stitches if they exist
        this.drawStitches(ctx, transform, globalStitchIndex, stitchOffset);

        // Debug drawing of start/end markers for each chain
        if (this.stitchLists && this.stitchLists.length > 0) {
            ctx.save();
            const markerSize = 15 / transform.scale;
            ctx.font = `${12 / transform.scale}px Arial`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            this.stitchLists.forEach((list, index) => {
                if (list.length < 2) return;

                // Start: Blue Circle
                const start = list[0];
                ctx.beginPath();
                ctx.arc(start.x, start.y, markerSize, 0, Math.PI * 2);
                ctx.fillStyle = 'blue';
                ctx.fill();
                ctx.strokeStyle = 'white';
                ctx.lineWidth = 1 / transform.scale;
                ctx.stroke();

                ctx.fillStyle = 'white';
                ctx.fillText(index.toString(), start.x, start.y);

                // End: Magenta Square
                const end = list[list.length - 1];
                ctx.beginPath();
                ctx.rect(end.x - markerSize, end.y - markerSize, markerSize * 2, markerSize * 2);
                ctx.fillStyle = 'magenta';
                ctx.fill();
                ctx.strokeStyle = 'white';
                ctx.lineWidth = 1 / transform.scale;
                ctx.stroke();

                ctx.fillStyle = 'white';
                ctx.fillText(index.toString(), end.x, end.y);
            });
            ctx.restore();
        }

        // Debug drawing of parallel lines
        if (this.debugLines && this.debugLines.length > 0) {
            ctx.save();
            ctx.beginPath();
            for (const line of this.debugLines) {
                ctx.moveTo(line.x1, line.y1);
                ctx.lineTo(line.x2, line.y2);
            }
            ctx.strokeStyle = 'rgba(0, 255, 0, 0.2)';
            ctx.lineWidth = 0.5 / transform.scale;
            ctx.stroke();
            ctx.restore();
        }

        // Debug drawing of clipped segments
        if (this.debugSegments && this.debugSegments.length > 0) {
            ctx.save();
            ctx.font = `${8 / transform.scale}px Arial`;
            for (let i = 0; i < this.debugSegments.length; i++) {
                const seg = this.debugSegments[i];
                const isUsed = this.debugUsedSegments && this.debugUsedSegments[i];

                ctx.beginPath();
                ctx.moveTo(seg.x1, seg.y1);
                ctx.lineTo(seg.x2, seg.y2);
                ctx.strokeStyle = isUsed ? 'rgba(255, 0, 0, 0.6)' : 'rgba(255, 165, 0, 0.9)'; // Red if used, Orange/Gold if not
                ctx.lineWidth = (isUsed ? 1.0 : 2.0) / transform.scale;
                ctx.stroke();

                // Draw segment index for order debugging
                ctx.fillStyle = isUsed ? 'red' : 'orange';
                ctx.fillText(i.toString(), (seg.x1 + seg.x2) / 2, (seg.y1 + seg.y2) / 2);
            }
            ctx.restore();
        }

        ctx.save();
        ctx.beginPath();
        drawAllPaths();
        ctx.strokeStyle = this.isSelected ? 'red' : this.color;
        ctx.lineWidth = this.lineWidth / transform.scale;
        ctx.stroke();
        ctx.restore();
    }

    renderActivePoints(ctx, transform) {
        const pts = this.getControlPoints();
        if (!pts || pts.length === 0) return;
        pts.forEach(p => {
            ctx.beginPath();
            const radius = 5 / transform.scale;
            if (p.type === 'line') {
                ctx.rect(p.x - radius, p.y - radius, radius * 2, radius * 2);
            } else if (p.type === 'interpolative') {
                ctx.moveTo(p.x, p.y - radius);
                ctx.lineTo(p.x + radius, p.y);
                ctx.lineTo(p.x, p.y + radius);
                ctx.lineTo(p.x - radius, p.y);
                ctx.closePath();
            } else {
                ctx.arc(p.x, p.y, radius, 0, 2 * Math.PI);
            }
            ctx.fillStyle = 'red';
            ctx.fill();
        });
    }

    // Preview for creation (Rubber banding)
    static renderPreview(ctx, points, color, transform) {
        if (points.length < 2) return;

        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y);

        for (let i = 1; i < points.length; i++) {
            const curr = points[i];
            const prev = points[i - 1];
            if (curr.type === 'line') {
                ctx.lineTo(curr.x, curr.y);
            } else if (curr.type === 'interpolative') {
                // Simplified preview for Interpolative (Catmull-Rom is complex to preview without prev/next context fully established)
                // Just draw line for preview or try basic curve?
                // Let's draw it as line for responsiveness in preview or simple quad
                ctx.lineTo(curr.x, curr.y);
            } else {
                let next = (i < points.length - 1) ? points[i + 1] : null;
                let tx, ty;
                if (next) {
                    tx = (curr.x + next.x) / 2;
                    ty = (curr.y + next.y) / 2;
                } else {
                    tx = curr.x;
                    ty = curr.y;
                }
                ctx.quadraticCurveTo(curr.x, curr.y, tx, ty);
            }
        }

        ctx.strokeStyle = color;
        ctx.lineWidth = 1 / transform.scale;
        ctx.stroke();
    }

    isPointInside(px, py, transform) {
        // Re-implement path tracing for hit test

        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d');
        const path = new Path2D();

        const tracePath = (pts) => {
            if (pts.length < 2) return;
            path.moveTo(pts[0].x, pts[0].y);
            for (let i = 1; i < pts.length; i++) {
                const curr = pts[i];
                const prev = pts[i - 1];

                if (curr.type === 'line') {
                    path.lineTo(curr.x, curr.y);
                } else if (curr.type === 'interpolative') {
                    let p0 = (i > 1) ? pts[i - 2] : prev;
                    let p1 = prev;
                    let p2 = curr;
                    let p3 = (i < pts.length - 1) ? pts[i + 1] : curr;
                    const tension = this.tension;
                    const cp1x = p1.x + (p2.x - p0.x) / 6 * tension;
                    const cp1y = p1.y + (p2.y - p0.y) / 6 * tension;
                    const cp2x = p2.x - (p3.x - p1.x) / 6 * tension;
                    const cp2y = p2.y - (p3.y - p1.y) / 6 * tension;
                    path.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, p2.x, p2.y);
                } else {
                    let next = (i < pts.length - 1) ? pts[i + 1] : null;
                    let tx, ty;
                    if (next) {
                        tx = (curr.x + next.x) / 2;
                        ty = (curr.y + next.y) / 2;
                    } else {
                        tx = curr.x;
                        ty = curr.y;
                    }
                    path.quadraticCurveTo(curr.x, curr.y, tx, ty);
                }
            }
            // Close if needed
            if (pts.length > 2 && pts[0].x === pts[pts.length - 1].x && pts[0].y === pts[pts.length - 1].y) {
                path.closePath();
            }
        };

        if (this.contours) {
            for (let poly of this.contours) {
                for (let ring of poly) {
                    tracePath(ring);
                }
            }
            // Check fill with evenodd rule if filled
            if (this.fillColor && tempCtx.isPointInPath(path, px, py, 'evenodd')) return true;
        } else {
            tracePath(this.points);
            const isClosed = this.points.length > 2 &&
                this.points[0].x === this.points[this.points.length - 1].x &&
                this.points[0].y === this.points[this.points.length - 1].y;
            if (isClosed && this.fillColor && tempCtx.isPointInPath(path, px, py)) return true;
        }

        const tolerance = 5 / transform.scale;
        tempCtx.lineWidth = (this.lineWidth + 10) / transform.scale;
        return tempCtx.isPointInStroke(path, px, py);
    }

    // Convert string/curve segments to a flat polygon (array of points)
    // Used for boolean operations to preserve shape approximation
    toPolygon() {
        const sampleCurve = (points) => {
            const res = [];
            if (points.length < 2) return res;

            const isClosed = points.length > 2 &&
                points[0].x === points[points.length - 1].x &&
                points[0].y === points[points.length - 1].y;

            // Start point approximation
            // Using logic from renderDrawing
            let startX = points[0].x;
            let startY = points[0].y;
            if (isClosed && points[1].type === 'curve') {
                startX = (points[0].x + points[1].x) / 2;
                startY = (points[0].y + points[1].y) / 2;
            }
            res.push({ x: startX, y: startY });

            for (let i = 1; i < points.length; i++) {
                const curr = points[i];
                const prev = points[i - 1];

                if (curr.type === 'line') {
                    res.push({ x: curr.x, y: curr.y });
                } else if (curr.type === 'interpolative') {
                    // Catmull-Rom
                    let p0, p3;
                    if (i > 1) { p0 = points[i - 2]; }
                    else if (isClosed) { p0 = points[points.length - 2]; }
                    else { p0 = prev; }

                    if (i < points.length - 1) { p3 = points[i + 1]; }
                    else if (isClosed) { p3 = points[1]; }
                    else { p3 = curr; }

                    // Sample Catmull-Rom
                    const p1 = prev;
                    const p2 = curr;
                    const tension = this.tension;
                    // Bezier control points
                    const cp1x = p1.x + (p2.x - p0.x) / 6 * tension;
                    const cp1y = p1.y + (p2.y - p0.y) / 6 * tension;
                    const cp2x = p2.x - (p3.x - p1.x) / 6 * tension;
                    const cp2y = p2.y - (p3.y - p1.y) / 6 * tension;

                    // Sample cubic Bezier
                    const steps = 10;
                    for (let t = 1; t <= steps; t++) {
                        const tt = t / steps;
                        const u = 1 - tt;
                        const x = u * u * u * p1.x + 3 * u * u * tt * cp1x + 3 * u * tt * tt * cp2x + tt * tt * tt * p2.x;
                        const y = u * u * u * p1.y + 3 * u * u * tt * cp1y + 3 * u * tt * tt * cp2y + tt * tt * tt * p2.y;
                        res.push({ x, y });
                    }
                } else {
                    // Quadratic Spline
                    let next = (i < points.length - 1) ? points[i + 1] : null;
                    let targetX, targetY;

                    if (isClosed && i === points.length - 1) {
                        targetX = startX; targetY = startY;
                    } else if (next) {
                        if (next.type === 'line' || next.type === 'interpolative') {
                            targetX = curr.x; targetY = curr.y;
                        } else {
                            targetX = (curr.x + next.x) / 2;
                            targetY = (curr.y + next.y) / 2;
                        }
                    } else {
                        targetX = curr.x; targetY = curr.y;
                    }

                    // Sample quadratic Bezier
                    // p1=prev(implicit start, roughly), p2=control(curr), p3=target
                    // Actually, for quadratic curveTo logic:
                    // Start point is current pen position (last point in res)
                    // Control point is curr.x, curr.y
                    // End point is targetX, targetY
                    const last = res[res.length - 1]; // current pen pos
                    const p1 = { x: last.x, y: last.y };
                    const p2 = { x: curr.x, y: curr.y };
                    const p3 = { x: targetX, y: targetY };

                    const steps = 10;
                    for (let t = 1; t <= steps; t++) {
                        const tt = t / steps;
                        const u = 1 - tt;
                        const x = u * u * p1.x + 2 * u * tt * p2.x + tt * tt * p3.x;
                        const y = u * u * p1.y + 2 * u * tt * p2.y + tt * tt * p3.y;
                        res.push({ x, y });
                    }
                }
            }
            return res;
        };

        if (this.contours) {
            return this.contours.map(poly => poly.map(ring => {
                const sampled = sampleCurve(ring);
                return sampled.map(p => [p.x, p.y]);
            }));
        } else {
            const sampled = sampleCurve(this.points);
            // Return as MultiPolygon structure: [[[x,y]...]]
            return [[sampled.map(p => [p.x, p.y])]];
        }
    }


    generateParallelStitches(bounds, polygon) {
        this.stitchLists = [];


    }

    /**
     * Compute parallel‑fill segments inside the PolySpline and connect them into a single chain.
     *
     * @param {number} spacing - Distance between parallel lines.
     * @param {number} angle - Angle of lines in radians.
     * @param {number} segmentLength - Max allowed length of connector between segments.
     * @returns {Array<{x: number, y: number}>} - Ordered points of the single chain (stitch path).
     */
    generateParallelChain(spacing, angle, segmentLength) {
        const bounds = this.getBounds(); // must be implemented; see helper below
        if (!bounds) return [];

        const multiPoly = this.toPolygon();
        const lines = this._generateParallelLines(bounds, spacing, angle / 180 * Math.PI);
        // this.debugLines = lines; // Store for debug rendering
        const segments = [];

        // 1. Intersect each parallel line with the filled area
        for (const line of lines) {
            const segs = this._clipLineToFilledArea(line, multiPoly);
            segments.push(...segs);
        }
        // this.debugSegments = segments; // Store for debug rendering

        if (segments.length === 0) return [];

        // 2. Build graph and connect segments into a single chain
        return this._connectSegmentsIntoChain(segments, segmentLength, multiPoly);
    }

    // --- Helper: get axis‑aligned bounding box of the shape
    getBounds() {
        let minX = Infinity, maxX = -Infinity;
        let minY = Infinity, maxY = -Infinity;

        const updateBounds = (p) => {
            if (p.x < minX) minX = p.x;
            if (p.x > maxX) maxX = p.x;
            if (p.y < minY) minY = p.y;
            if (p.y > maxY) maxY = p.y;
        };

        if (this.contours) {
            this.contours.forEach((poly) =>
                poly.forEach((ring) => ring.forEach(updateBounds))
            );
        } else if (this.points) {
            this.points.forEach(updateBounds);
        }

        if (minX === Infinity) return null;
        return { minX, maxX, minY, maxY };
    }

    // --- Helper: generate infinite‑like parallel lines in the bounding box
    _generateParallelLines(bounds, spacing, angle) {
        const { minX, maxX, minY, maxY } = bounds;
        const cosA = Math.cos(angle);
        const sinA = Math.sin(angle);

        const dx = cosA * spacing;
        const dy = sinA * spacing;

        const normalX = -sinA;
        const normalY = cosA;

        const len = Math.hypot(maxX - minX, maxY - minY) * 2;

        const lines = [];
        let offset = 0;

        // Move along the normal direction and create lines
        while (true) {
            const cx = (minX + maxX) / 2 + normalX * offset;
            const cy = (minY + maxY) / 2 + normalY * offset;

            const x1 = cx - cosA * len;
            const y1 = cy - sinA * len;
            const x2 = cx + cosA * len;
            const y2 = cy + sinA * len;

            lines.push({ x1, y1, x2, y2, offset });

            offset += spacing;
            if (offset > len) break;
        }

        // Negative offsets too
        offset = -spacing;
        while (offset > -len) {
            const cx = (minX + maxX) / 2 + normalX * offset;
            const cy = (minY + maxY) / 2 + normalY * offset;

            const x1 = cx - cosA * len;
            const y1 = cy - sinA * len;
            const x2 = cx + cosA * len;
            const y2 = cy + sinA * len;

            lines.push({ x1, y1, x2, y2, offset });

            offset -= spacing;
        }

        return lines.sort((a, b) => a.offset - b.offset);
    }

    // --- Helper: intersect a line with the filled area (outer minus holes)
    _clipLineToFilledArea(line, multiPoly) {
        if (!multiPoly || multiPoly.length === 0) return [];

        const allSegments = [];

        for (const poly of multiPoly) {
            const intersectionPoints = [];
            for (const ring of poly) {
                if (!ring || ring.length < 3) continue;

                for (let i = 0; i < ring.length; i++) {
                    const p1 = { x: ring[i][0], y: ring[i][1] };
                    const nextIdx = (i + 1) % ring.length;
                    const p2 = { x: ring[nextIdx][0], y: ring[nextIdx][1] };

                    const is = lineSegmentIntersect(
                        line.x1, line.y1, line.x2, line.y2,
                        p1.x, p1.y, p2.x, p2.y
                    );
                    if (is) {
                        // Avoid duplicates at vertices
                        if (!intersectionPoints.some(p => Math.hypot(p.x - is.x, p.y - is.y) < 1e-6)) {
                            intersectionPoints.push(is);
                        }
                    }
                }
            }

            if (intersectionPoints.length < 2) continue;

            // Sort by position along the line
            intersectionPoints.sort((a, b) => {
                const da = (a.x - line.x1) * (line.x2 - line.x1) + (a.y - line.y1) * (line.y2 - line.y1);
                const db = (b.x - line.x1) * (line.x2 - line.x1) + (b.y - line.y1) * (line.y2 - line.y1);
                return da - db;
            });

            // Even-odd rule: pairs of points form segments inside the filled region
            for (let i = 0; i < intersectionPoints.length - 1; i += 2) {
                allSegments.push({
                    x1: intersectionPoints[i].x,
                    y1: intersectionPoints[i].y,
                    x2: intersectionPoints[i + 1].x,
                    y2: intersectionPoints[i + 1].y,
                });
            }
        }

        return allSegments;
    }

    // --- Helper: connect segments into a single chain
    _connectSegmentsIntoChain(segments, segmentLength, multiPoly) {
        if (segments.length === 0) return [];

        const allChains = [];
        const used = new Array(segments.length).fill(false);
        this.debugUsedSegments = used;

        while (true) {
            // Find first unused segment to start a new chain
            let firstUnused = -1;
            for (let i = 0; i < used.length; i++) {
                if (!used[i]) {
                    firstUnused = i;
                    break;
                }
            }
            if (firstUnused === -1) break;

            let currentChain = [];
            let currentSegIndex = firstUnused;
            let currentIsEnd = false;

            while (currentSegIndex !== -1) {
                const seg = segments[currentSegIndex];
                if (!used[currentSegIndex]) {
                    const xStart = currentIsEnd ? seg.x2 : seg.x1;
                    const yStart = currentIsEnd ? seg.y2 : seg.y1;
                    const xEnd = currentIsEnd ? seg.x1 : seg.x2;
                    const yEnd = currentIsEnd ? seg.y1 : seg.y2;

                    const dx = xEnd - xStart;
                    const dy = yEnd - yStart;
                    const dist = Math.hypot(dx, dy);
                    const numSteps = Math.max(1, Math.ceil(dist / segmentLength));

                    // Add connection from last point to start of this segment
                    const last = currentChain[currentChain.length - 1];
                    if (last && Math.hypot(last.x - xStart, last.y - yStart) > 1e-6) {
                        const connectionPath = this._getCurvePath(last, { x: xStart, y: yStart }, multiPoly, segmentLength);
                        currentChain.push(...connectionPath);
                    } else if (!last) {
                        currentChain.push({ x: xStart, y: yStart });
                    }

                    for (let i = 1; i <= numSteps; i++) {
                        const t = i / numSteps;
                        currentChain.push({
                            x: xStart + dx * t,
                            y: yStart + dy * t
                        });
                    }
                    used[currentSegIndex] = true;
                }

                let nextSegIndex = -1;
                let nextIsStart = false;
                let minDist = Infinity;

                const lastPt = currentChain[currentChain.length - 1];

                const baseThreshold = this.spacing * 5;
                const edgeThreshold = segmentLength * EDGE_CONNECTION_N;

                for (let i = 0; i < segments.length; i++) {
                    if (used[i]) continue;

                    const seg = segments[i];

                    const checkDist = (tx, ty, isStart) => {
                        const d = Math.hypot(lastPt.x - tx, lastPt.y - ty);
                        if (d < minDist) {
                            // Connect if within base threshold OR if within edge threshold AND along edge
                            if (d < baseThreshold || (d < edgeThreshold && this._isAlongEdge(lastPt, { x: tx, y: ty }, multiPoly))) {
                                minDist = d;
                                nextSegIndex = i;
                                nextIsStart = isStart;
                            }
                        }
                    };

                    checkDist(seg.x1, seg.y1, true);
                    checkDist(seg.x2, seg.y2, false);
                }
                currentSegIndex = nextSegIndex;
                currentIsEnd = !nextIsStart;
            }
            if (currentChain.length > 0) {
                allChains.push(currentChain);
            }
        }

        return allChains;
    }

    _isAlongEdge(p1, p2, multiPoly) {
        const midX = (p1.x + p2.x) / 2;
        const midY = (p1.y + p2.y) / 2;

        let minDistSq = Infinity;
        for (const poly of multiPoly) {
            for (const ring of poly) {
                for (let i = 0; i < ring.length; i++) {
                    const nextIdx = (i + 1) % ring.length;
                    const v1 = { x: ring[i][0], y: ring[i][1] };
                    const v2 = { x: ring[nextIdx][0], y: ring[nextIdx][1] };

                    const dSq = this._distToSegmentSquared({ x: midX, y: midY }, v1, v2);
                    if (dSq < minDistSq) minDistSq = dSq;
                }
            }
        }
        return Math.sqrt(minDistSq) < this.spacing * 1.5;
    }

    _distToSegmentSquared(p, v, w) {
        const l2 = (v.x - w.x) ** 2 + (v.y - w.y) ** 2;
        if (l2 === 0) return (p.x - v.x) ** 2 + (p.y - v.y) ** 2;
        let t = ((p.x - v.x) * (w.x - v.x) + (p.y - v.y) * (w.y - v.y)) / l2;
        t = Math.max(0, Math.min(1, t));
        return (p.x - (v.x + t * (w.x - v.x))) ** 2 + (p.y - (v.y + t * (w.y - v.y))) ** 2;
    }

    _getCurvePath(p1, p2, multiPoly, segmentLength) {
        let bestRing = null;
        let pos1 = null;
        let pos2 = null;
        let minDistSum = Infinity;

        for (const poly of multiPoly) {
            for (const ring of poly) {
                const rPos1 = this._findClosestPositionOnRing(p1, ring);
                const rPos2 = this._findClosestPositionOnRing(p2, ring);
                if (!rPos1 || !rPos2) continue;

                const d1 = Math.hypot(p1.x - rPos1.x, p1.y - rPos1.y);
                const d2 = Math.hypot(p2.x - rPos2.x, p2.y - rPos2.y);

                if (d1 < this.spacing * 2 && d2 < this.spacing * 2) {
                    if (d1 + d2 < minDistSum) {
                        minDistSum = d1 + d2;
                        bestRing = ring;
                        pos1 = rPos1;
                        pos2 = rPos2;
                    }
                }
            }
        }

        if (!bestRing) {
            return this._subdivideSegment(p1, p2, segmentLength);
        }

        return this._getPathOnRing(pos1, pos2, bestRing, segmentLength);
    }

    _findClosestPositionOnRing(p, ring) {
        let minDistSq = Infinity;
        let bestPos = null;
        // Assume ring is closed and ring[0] == ring[last], so we check ring.length - 1 edges
        const n = ring.length;
        for (let i = 0; i < n - 1; i++) {
            const v1 = { x: ring[i][0], y: ring[i][1] };
            const v2 = { x: ring[i + 1][0], y: ring[i + 1][1] };

            const l2 = (v1.x - v2.x) ** 2 + (v1.y - v2.y) ** 2;
            if (l2 === 0) continue;
            let t = ((p.x - v1.x) * (v2.x - v1.x) + (p.y - v1.y) * (v2.y - v1.y)) / l2;
            t = Math.max(0, Math.min(1, t));
            const projX = v1.x + t * (v2.x - v1.x);
            const projY = v1.y + t * (v2.y - v1.y);
            const dSq = (p.x - projX) ** 2 + (p.y - projY) ** 2;
            if (dSq < minDistSq) {
                minDistSq = dSq;
                bestPos = { edgeIndex: i, t: t, x: projX, y: projY };
            }
        }
        return bestPos;
    }

    _getPathOnRing(pos1, pos2, ring, segmentLength) {
        const n = ring.length; // ring[0] == ring[n-1]
        const numEdges = n - 1;
        const pts = ring.map(r => ({ x: r[0], y: r[1] }));

        const getPoints = (p1, p2, dir) => {
            const res = [];
            let curr = p1.edgeIndex;
            if (dir > 0) {
                // Forward
                if (p1.edgeIndex === p2.edgeIndex && p1.t <= p2.t) {
                    // Same edge, direct
                } else {
                    // To end of current edge
                    res.push(pts[curr + 1]);
                    curr = (curr + 1) % numEdges;
                    while (curr !== p2.edgeIndex) {
                        res.push(pts[curr + 1]);
                        curr = (curr + 1) % numEdges;
                    }
                }
            } else {
                // Backward
                if (p1.edgeIndex === p2.edgeIndex && p1.t >= p2.t) {
                    // Same edge, direct
                } else {
                    // To start of current edge
                    res.push(pts[curr]);
                    while (curr !== p2.edgeIndex) {
                        curr = (curr - 1 + numEdges) % numEdges;
                        res.push(pts[curr]);
                    }
                }
            }
            res.push({ x: p2.x, y: p2.y });

            // Calculate distance
            let d = 0;
            let lp = { x: p1.x, y: p1.y };
            for (const p of res) {
                d += Math.hypot(p.x - lp.x, p.y - lp.y);
                lp = p;
            }
            return { res, d };
        };

        const f = getPoints(pos1, pos2, 1);
        const b = getPoints(pos1, pos2, -1);

        const best = f.d < b.d ? f.res : b.res;
        const final = [];
        let lp = { x: pos1.x, y: pos1.y };
        for (const p of best) {
            final.push(...this._subdivideSegment(lp, p, segmentLength));
            lp = p;
        }
        return final;
    }

    _subdivideSegment(p1, p2, segmentLength) {
        const dx = p2.x - p1.x;
        const dy = p2.y - p1.y;
        const dist = Math.hypot(dx, dy);
        const steps = Math.max(1, Math.ceil(dist / segmentLength));
        const pts = [];
        for (let i = 1; i <= steps; i++) {
            const t = i / steps;
            pts.push({ x: p1.x + dx * t, y: p1.y + dy * t });
        }
        return pts;
    }
}

// --- Helper: line segment intersection (returns intersection point or null)
function lineSegmentIntersect(x1, y1, x2, y2, x3, y3, x4, y4) {
    const denom = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);
    if (Math.abs(denom) < 1e-10) return null;

    const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denom;
    const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denom;

    if (ua < 0 || ua > 1 || ub < 0 || ub > 1) return null;

    return {
        x: x1 + ua * (x2 - x1),
        y: y1 + ua * (y2 - y1),
    };
}
